/**
 * CogoService — Professional Coordinate Geometry Engine
 *
 * Implements core surveying calculations used daily by land surveyors:
 * - Bearing + Distance (forward)
 * - Bearing-Bearing intersection
 * - Distance-Distance intersection
 * - Traverse closure + precision reporting
 * - Inverse (point to point)
 *
 * All angles in decimal degrees. Bearings measured clockwise from North (0° = North).
 * This is the mathematical heart for surveyors in GeoVera "Future".
 */

export interface Point2D {
  x: number; // Easting
  y: number; // Northing
}

export interface CogoResult {
  success: boolean;
  point?: Point2D;
  message: string;
  metadata?: Record<string, any>;
}

export interface TraversePoint {
  id: string;
  station: string;
  northing: number;
  easting: number;
  elevation?: number;
  description?: string;
}

export interface TraverseClosure {
  linearMisclosure: number;
  totalLength: number;
  relativePrecision: number;
  deltaE: number;
  deltaN: number;
}

export class CogoService {
  private toRadians(deg: number): number {
    return (deg * Math.PI) / 180;
  }

  private toDegrees(rad: number): number {
    return (rad * 180) / Math.PI;
  }

  /**
   * Bearing + Distance from a known point.
   * Bearing: 0° = North, clockwise positive (standard survey convention).
   */
  bearingDistance(from: Point2D, bearingDeg: number, distance: number): CogoResult {
    if (!isFinite(distance) || distance <= 0) {
      return { success: false, message: 'Distance must be positive' };
    }
    const brg = this.toRadians(bearingDeg);
    // In math: 0° = East, but survey 0° = North. Rotate by 90°.
    const dx = distance * Math.sin(brg);
    const dy = distance * Math.cos(brg);

    const to: Point2D = {
      x: from.x + dx,
      y: from.y + dy,
    };

    return {
      success: true,
      point: to,
      message: `Point computed at ${distance.toFixed(3)} m on bearing ${bearingDeg.toFixed(4)}°`,
      metadata: { bearing: bearingDeg, distance },
    };
  }

  /**
   * Bearing-Bearing intersection (classic survey intersection).
   */
  bearingBearingIntersection(
    p1: Point2D,
    bearing1: number,
    p2: Point2D,
    bearing2: number
  ): CogoResult {
    const b1 = this.toRadians(bearing1);
    const b2 = this.toRadians(bearing2);

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    // Direction vectors
    const dir1x = Math.sin(b1);
    const dir1y = Math.cos(b1);
    const dir2x = Math.sin(b2);
    const dir2y = Math.cos(b2);

    const denom = dir1x * dir2y - dir1y * dir2x;

    if (Math.abs(denom) < 1e-9) {
      return {
        success: false,
        message: 'Lines are parallel or coincident — no unique intersection',
      };
    }

    const t = (dx * dir2y - dy * dir2x) / denom;

    const ix = p1.x + t * dir1x;
    const iy = p1.y + t * dir1y;

    return {
      success: true,
      point: { x: ix, y: iy },
      message: 'Bearing-Bearing intersection computed',
      metadata: { t },
    };
  }

  /**
   * Distance-Distance intersection (two circles). Returns both possible solutions.
   */
  distanceDistanceIntersection(p1: Point2D, d1: number, p2: Point2D, d2: number): CogoResult {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const d = Math.hypot(dx, dy);

    if (d > d1 + d2 || d < Math.abs(d1 - d2) || d === 0) {
      return {
        success: false,
        message: 'No real intersection (circles do not intersect or are coincident)',
      };
    }

    const a = (d1 * d1 - d2 * d2 + d * d) / (2 * d);
    const h = Math.sqrt(d1 * d1 - a * a);

    const xm = p1.x + (a * dx) / d;
    const ym = p1.y + (a * dy) / d;

    const xs1 = xm + (h * dy) / d;
    const ys1 = ym - (h * dx) / d;
    const xs2 = xm - (h * dy) / d;
    const ys2 = ym + (h * dx) / d;

    return {
      success: true,
      point: { x: xs1, y: ys1 }, // primary solution
      message: `Two solutions. Primary: (${xs1.toFixed(3)}, ${ys1.toFixed(3)})`,
      metadata: {
        solution1: { x: xs1, y: ys1 },
        solution2: { x: xs2, y: ys2 },
      },
    };
  }

  /**
   * Compute traverse closure from a list of coordinate points.
   * Assumes the first and last points should close (or computes misclosure).
   */
  computeTraverseClosure(points: TraversePoint[]): TraverseClosure {
    if (points.length < 2) {
      return {
        linearMisclosure: 0,
        totalLength: 0,
        relativePrecision: Infinity,
        deltaE: 0,
        deltaN: 0,
      };
    }

    let totalLength = 0;
    let prev = points[0];

    for (let i = 1; i < points.length; i++) {
      const curr = points[i];
      const leg = Math.hypot(curr.easting - prev.easting, curr.northing - prev.northing);
      totalLength += leg;
      prev = curr;
    }

    const first = points[0];
    const last = points[points.length - 1];

    const deltaE = last.easting - first.easting;
    const deltaN = last.northing - first.northing;
    const linearMisclosure = Math.hypot(deltaE, deltaN);

    const relativePrecision =
      totalLength > 0 ? Math.round(totalLength / linearMisclosure) : Infinity;

    return {
      linearMisclosure,
      totalLength,
      relativePrecision,
      deltaE,
      deltaN,
    };
  }

  /**
   * Inverse: compute bearing and distance between two points.
   */
  inverse(
    p1: Point2D,
    p2: Point2D
  ): { bearing: number; distance: number; deltaE: number; deltaN: number } {
    const de = p2.x - p1.x;
    const dn = p2.y - p1.y;
    const dist = Math.hypot(de, dn);
    let brg = this.toDegrees(Math.atan2(de, dn)); // atan2(east, north)
    if (brg < 0) brg += 360;
    return { bearing: brg, distance: dist, deltaE: de, deltaN: dn };
  }
}

// Singleton
export const cogoService = new CogoService();
